import {
  Co6TransactionTypes,
  InternalTransferSubmit,
  MultiInheritanceBackupWithholdingSectionPayload,
  RegularISubmit,
} from '@interfaces';
import { FlowTypes, InternalTransferReceiptDTO } from '@interfaces/models';
import { VgEmploymentState } from '@interfaces/models/open-account-mfes/employment-mfe.model';
import { createSelector } from '@ngrx/store';
import { TaxClassification } from '@src/app/secure/terms/components/backup-withholding/backup-withholding.component';
import {
  getDecedentInfoState,
  getFeatureToggleState,
  getInternalTransferState,
  getMfePersonalAddressState,
  getOpenAccountState,
  getSelectedClientAccountsState,
  getSubmitState,
  getTriageState,
} from '@store/reducers';
import { EstateState } from '@store/reducers/estate/estate.reducer';
import { getClientName } from '@store/reducers/personal-data/personal-data.reducer';
import { ISubmitState } from '@store/reducers/submit/submit.reducer';
import { getTransactionsForTaxClassification } from '@store/reducers/transaction-state/transaction-state.reducer';
import { getIsTrustToTrustFlow } from '@store/reducers/triage/triage.reducer';
import { TrustTaxInfoState } from '@store/reducers/trust-tax-info/trust-tax-info.reducer';
import { selectEmploymentMfeDataByOwnerIndex } from '@store/selectors/open-account/open-account.selectors';
import { selectMfePersonalAddress } from '@store/selectors/personal-info-address/personal-info-address.selectors';
import {
  selectClientPersonalInfoData,
  selectEmploymentFeature,
  selectPersonalAddressFeature,
} from 'vg-interaction-model/dist';

import { AccordionId } from '../../reducers/terms-accordion/terms-accordion.reducer';
import { selectBeneficiaryState } from '../beneficiaries/beneficiaries.selectors';
import { selectSelectedPositions } from '../cost-basis/cost-basis.selectors';
import { selectEstateState } from '../estate/estate.selectors';
import { selectGiftingState } from '../gifting/gifting.selectors';
import { selectInheritedIraState } from '../inherited-ira/inherited-ira.selectors';
import {
  selectMfePersonalInfoClientPersonalInfo,
  selectMfePersonalInfoClientSSN,
} from '../mfe-personal-info-client/mfe-personal-info-client.selectors';
import { selectMultiAccordions } from '../multi-terms-accordion/multi-terms-accordion.selectors';
import { selectEnrollPageHTML } from '../open-choice/open-choice.selectors';
import { selectOTPVerificationStatus } from '../otp/otp.selectors';
import {
  selectClientName,
  selectClientSegment,
  selectPersonalDataState,
} from '../personal-data/personal-data.selectors';
import { selectMfePhone } from '../personal-phone/personal-phone.selectors';
import { selectInternalTransferReceiptData } from '../receipt/internal-transfer/internal-transfer-receipt.selectors';
import { selectMultiInheritanceReceiptData } from '../receipt/multi-inheritance/multi-inheritance-receipt.selectors';
import { selectReceiptData } from '../receipt/receipt.selectors';
import {
  selectIsTaxDocumentInState,
  selectTaxCheckboxSelections,
} from '../tax-document-upload/tax-document-upload.selectors';
import { selectTCFTrustState } from '../tcf-trust/tcf-trust.selectors';
import { selectAccordionAcceptedDate } from '../terms-accordion/terms-accordion.selectors';
import {
  selectAllNonW8TrustGroups,
  selectAllTransactions,
  selectBackupWithholdingAnswer,
  selectDistinctNonW8EstateGroups,
  selectHasIndividualOrJointNonEstateTrustTransaction,
  selectHasIraNonEstateTrustTransaction,
  selectHasW8Transaction,
} from '../transaction-state/transaction-state.selectors';
import { selectTrustState } from '../trust/trust.selectors';
import { selectTrustTaxInfoState } from '../trust-tax-info/trust-tax-info.selector';
import {
  buildT2TTermsReceipt,
  buildTermsAcceptance,
  checkForFlowTypes,
  createMultiSubmitPayload,
  createReceiptDTO,
} from './submit.helpers';

export const selectIsSubmitInProgress = createSelector(getSubmitState, (state: ISubmitState) => {
  return state.isSubmitInProgress;
});

export const selectIsStp = createSelector(getSubmitState, (state: ISubmitState) => {
  return state.isStp;
});

export const selectIsSubmitSuccessful = createSelector(getSubmitState, (state: ISubmitState) => {
  return state.isSubmitSuccessful;
});

export const getSubmitPayload = createSelector(
  selectMfePersonalInfoClientPersonalInfo,
  selectPersonalDataState,
  getDecedentInfoState,
  getFeatureToggleState,
  getTriageState,
  getOpenAccountState,
  selectReceiptData,
  selectGiftingState,
  selectInheritedIraState,
  selectSelectedPositions,
  getSelectedClientAccountsState,
  selectEstateState,
  selectTrustState,
  selectTrustTaxInfoState,
  selectTCFTrustState,
  selectOTPVerificationStatus,
  selectEnrollPageHTML,
  selectEmploymentMfeDataByOwnerIndex(0),
  selectMfePersonalAddress,
  selectMfePhone,
  selectBeneficiaryState,
  selectAccordionAcceptedDate(AccordionId.DOCUMENTS_AND_ELECTRONIC_CONSENT),
  selectAccordionAcceptedDate(AccordionId.BACKUP_WITHHOLDING),
  selectAccordionAcceptedDate(AccordionId.SIGN_APPLICATION),
  selectMfePersonalInfoClientSSN,
  selectMultiAccordions,
  (
    clientPersonalInfoData,
    personalData,
    decedentInfo,
    featureToggles,
    triageState,
    openAccountState,
    receiptDTO,
    giftingState,
    inheritedIraState,
    selectedPositions,
    selectedClientAccounts,
    estateInfo,
    trustState,
    trustTaxInfoState,
    tcfTrustState,
    otpVerificationStatus,
    enrollPageHTML,
    employmentFeature,
    address,
    phone,
    beneficiaryState,
    documentsAndElectronicConsentAcceptedDate,
    backupWithholdingAcceptedDate,
    signApplicationAcceptedDate,
    ssn,
    multiAccordions,
    // eslint-disable-next-line max-params
  ): RegularISubmit => {
    const transactionType = checkForFlowTypes(triageState.flowType);
    const isMultiFlow = triageState.flowType === FlowTypes.MULTI_INHERITANCE;
    const isTrustToTrustFlow = getIsTrustToTrustFlow(triageState);

    const estateInfoPayload =
      featureToggles.cwp_inheritance_new_terms && isMultiFlow
        ? ((({ withholdingQuestion, signature, date, ...rest }) => rest)(
            estateInfo ?? ({} as EstateState),
          ) as typeof estateInfo)
        : estateInfo;

    const trustTaxInfoPayload =
      featureToggles.cwp_inheritance_new_terms && (isMultiFlow || isTrustToTrustFlow)
        ? ((({ withholdingQuestion, signature, date, ...rest }) => rest)(
            trustTaxInfoState ?? ({} as TrustTaxInfoState),
          ) as typeof trustTaxInfoState)
        : trustTaxInfoState;
    const termsAcceptance = buildTermsAcceptance(multiAccordions);

    const termsReceipt =
      isTrustToTrustFlow && featureToggles.cwp_inheritance_new_terms
        ? buildT2TTermsReceipt(
            trustTaxInfoState?.backupWithholdingAnswer,
            trustTaxInfoState?.backupWithholdingSignatureName,
            termsAcceptance,
          )
        : undefined;

    return {
      config: {
        personalData: {
          ownerName: getClientName(clientPersonalInfoData),
          segment: personalData.segment,
          dateOfBirth: clientPersonalInfoData.birthDate,
          citizenshipStatus: clientPersonalInfoData.citizenshipStatus,
          ...(getIsTrustToTrustFlow(triageState) && {
            countryOfCitizenship: clientPersonalInfoData.countryOfCitizenship,
          }),
        },
        decedentInfo: decedentInfo,
        featureToggles: featureToggles,
        triage: triageState,
        openAccountState: openAccountState,
        selectedAccs: selectedClientAccounts,
        gifting: { ...giftingState, selectedPositions },
        inheritedIraState: inheritedIraState,
        estateInfo: estateInfoPayload,
        trust: trustState,
        trustTaxInfo: trustTaxInfoPayload,
        tcfTrust: tcfTrustState,
        employment:
          employmentFeature?.associations && employmentFeature?.employmentStatus
            ? {
                associations: employmentFeature.associations,
                employmentStatus: employmentFeature.employmentStatus,
              }
            : undefined,
        mailingAddress: address?.mailingAddress,
        streetAddress: address?.streetAddress,
        phone,
        beneficiaries: beneficiaryState,
        termsAcceptance,
        backupWithholdingSignatureName: trustTaxInfoState?.backupWithholdingSignatureName,
        backupWithholdingAnswer: trustTaxInfoState?.backupWithholdingAnswer,
      },
      receiptDTO: {
        ...createReceiptDTO(triageState, selectedClientAccounts, receiptDTO),
        ...(termsReceipt && { terms: termsReceipt }),
      },
      transactionType: transactionType,
      otpVerificationStatus: otpVerificationStatus,
      daEnrollPageHtml: enrollPageHTML || undefined,
      ...(featureToggles.cwp_terms_w9_redesign && {
        w9TermsAcceptedDates: {
          documentsAndElectronicConsentAcceptedDate,
          backupWithholdingAcceptedDate,
          signApplicationAcceptedDate,
          ssn: ssn ?? '',
        },
      }),
    };
  },
);

export const selectW9TermsAcceptedDates = createSelector(
  selectAccordionAcceptedDate(AccordionId.DOCUMENTS_AND_ELECTRONIC_CONSENT),
  selectAccordionAcceptedDate(AccordionId.BACKUP_WITHHOLDING),
  selectAccordionAcceptedDate(AccordionId.SIGN_APPLICATION),
  selectMfePersonalInfoClientSSN,
  (
    documentsAndElectronicConsentAcceptedDate,
    backupWithholdingAcceptedDate,
    signApplicationAcceptedDate,
    ssn,
  ) => ({
    documentsAndElectronicConsentAcceptedDate,
    backupWithholdingAcceptedDate,
    signApplicationAcceptedDate,
    ssn: ssn ?? '',
  }),
);

export const getITSubmitPayload = createSelector(
  selectClientName,
  selectClientSegment,
  getFeatureToggleState,
  getInternalTransferState,
  selectInternalTransferReceiptData(),
  getOpenAccountState,
  selectSelectedPositions,
  selectOTPVerificationStatus,
  selectW9TermsAcceptedDates,

  (
    ownerName,
    segment,
    featureToggles,
    internalTransferState,
    receiptDTO: InternalTransferReceiptDTO,
    openAccountState,
    selectedPositions,
    otpVerificationStatus,
    w9TermsAcceptedDates,
  ): InternalTransferSubmit => {
    const isVanguardCharitable =
      internalTransferState.transferFrom?.selectedAccount?.accountData?.isVanguardCharitable ===
      true;

    return {
      itConfig: {
        personalData: { ownerName, segment },
        featureToggles: featureToggles,
        internalTransfer: internalTransferState,
        openAccountState: openAccountState,
        selectedPositions: selectedPositions,
      },
      receiptDTO: receiptDTO,
      transactionType: isVanguardCharitable
        ? Co6TransactionTypes.vanguardCharitable
        : Co6TransactionTypes.internalTransfer,
      otpVerificationStatus: otpVerificationStatus,
      ...(featureToggles.cwp_terms_w9_redesign && {
        w9TermsAcceptedDates,
      }),
    };
  },
);

export const selectMultiInheritanceBackupWithholdingSections = createSelector(
  getFeatureToggleState,
  selectHasIraNonEstateTrustTransaction,
  selectHasIndividualOrJointNonEstateTrustTransaction,
  selectHasW8Transaction,
  selectAllNonW8TrustGroups,
  selectDistinctNonW8EstateGroups,
  selectMfePersonalInfoClientSSN,
  selectBackupWithholdingAnswer(TaxClassification.IRA),
  selectBackupWithholdingAnswer(TaxClassification.Individual),
  selectBackupWithholdingAnswer(TaxClassification.TRUST),
  selectBackupWithholdingAnswer(TaxClassification.ESTATE),
  selectAllTransactions,
  (
    featureToggles,
    hasIraSection,
    hasIndividualJointSection,
    hasW8Section,
    trustGroups,
    estateGroups,
    ssn,
    iraAnswer,
    individualAnswer,
    trustAnswer,
    estateAnswer,
    transactions,
  ): MultiInheritanceBackupWithholdingSectionPayload | undefined => {
    if (!featureToggles.cwp_inheritance_new_terms) {
      return undefined;
    }
    const sections: MultiInheritanceBackupWithholdingSectionPayload['sections'] = [];

    if (hasIraSection) {
      const iraTransaction = Array.isArray(transactions)
        ? getTransactionsForTaxClassification(
            transactions.filter((transaction) => transaction.accountTriage),
            TaxClassification.IRA,
          ).find((transaction) => transaction.backupWithholdingSignatureName)
        : undefined;
      sections.push({
        taxClassification: TaxClassification.IRA,
        backupWithholdingAnswer: iraAnswer ?? undefined,
        backupWithholdingSignatureName: iraTransaction?.backupWithholdingSignatureName,
      });
    }

    if (hasIndividualJointSection) {
      const individualTransaction = Array.isArray(transactions)
        ? getTransactionsForTaxClassification(
            transactions.filter((transaction) => transaction.accountTriage),
            TaxClassification.Individual,
          ).find((transaction) => transaction.backupWithholdingSignatureName)
        : undefined;
      sections.push({
        taxClassification: TaxClassification.Individual,
        backupWithholdingAnswer: individualAnswer ?? undefined,
        backupWithholdingSignatureName: individualTransaction?.backupWithholdingSignatureName,
      });
    }

    for (const group of trustGroups || []) {
      sections.push({
        taxClassification: TaxClassification.TRUST,
        backupWithholdingAnswer: trustAnswer ?? undefined,
        backupWithholdingSignatureName: Array.isArray(transactions)
          ? transactions.find(
              (transaction) =>
                transaction.backupWithholdingSignatureName &&
                transaction.trust?.trustName === group.trustName &&
                (group.ein
                  ? transaction.trustTaxInfo?.ein === group.ein
                  : transaction.trustTaxInfo?.ssn === group.grantorSsn),
            )?.backupWithholdingSignatureName
          : undefined,
      });
    }

    for (const group of estateGroups || []) {
      sections.push({
        taxClassification: TaxClassification.ESTATE,
        backupWithholdingAnswer: estateAnswer ?? undefined,
        backupWithholdingSignatureName: Array.isArray(transactions)
          ? transactions.find(
              (transaction) =>
                transaction.backupWithholdingSignatureName &&
                transaction.estate?.estateName === group.estateName &&
                transaction.estate?.tin_private === group.tin,
            )?.backupWithholdingSignatureName
          : undefined,
      });
    }

    if (hasW8Section) {
      sections.push({
        taxClassification: TaxClassification.W8,
      });
    }

    return {
      hasIraSection,
      hasIndividualJointSection,
      trustGroups: (trustGroups || []).map((group) => ({
        trustName: group.trustName,
        ein: group.ein,
        grantorName: group.grantorName,
        grantorSsn: group.grantorSsn,
        isGrantorTrust: group.isGrantorTrust,
        taxClassification: group.taxClassification,
      })),
      estateGroups: (estateGroups || []).map((group) => ({
        estateName: group.estateName,
        tin: group.tin,
      })),
      ssn,
      sections,
    };
  },
);

const selectMultiReceiptDTO = selectMultiInheritanceReceiptData(true);

export const getMultiSubmitPayload = createSelector(
  selectMfePersonalInfoClientPersonalInfo,
  selectPersonalDataState,
  getDecedentInfoState,
  getFeatureToggleState,
  getTriageState,
  getOpenAccountState,
  selectOTPVerificationStatus,
  selectEmploymentMfeDataByOwnerIndex(0),
  getMfePersonalAddressState,
  selectMfePhone,
  selectAllTransactions,
  selectMultiReceiptDTO,
  selectTaxCheckboxSelections,
  selectIsTaxDocumentInState,
  selectMultiAccordions,
  selectMultiInheritanceBackupWithholdingSections,

  (
    clientPersonalInfoData,
    personalData,
    decedentInfo,
    featureToggles,
    triageState,
    openAccountState,
    otpVerificationStatus,
    employmentFeature,
    address,
    phone,
    transactions,
    receiptMulti,
    taxCheckboxSelections,
    isTaxDocumentInState,
    multiAccordions,
    backupWithholdingSections,
    // eslint-disable-next-line max-params
  ): { payloads: RegularISubmit[] | null; error: string | null } => {
    try {
      const payloads = createMultiSubmitPayload(
        clientPersonalInfoData,
        personalData,
        decedentInfo,
        featureToggles,
        triageState,
        openAccountState,
        otpVerificationStatus,
        employmentFeature,
        address,
        phone,
        transactions || [],
        receiptMulti,
        taxCheckboxSelections,
        isTaxDocumentInState,
        multiAccordions,
        backupWithholdingSections,
      );
      return { payloads, error: null };
    } catch (e) {
      return {
        payloads: null,
        error: `getMultiSubmitPayload error: ${e instanceof Error ? e.message : String(e)}`,
      };
    }
  },
);
